/**
 * Enhanced API service helpers for consistent error handling and message parsing
 * Provides utilities to sync frontend with backend API responses
 */

import { AxiosResponse } from 'axios';
import { parseApiError, extractFieldErrors, formatErrorForLogging } from './responseParser';
import { useNotificationStore } from '@/zustand/useNotificationStore';

/**
 * Wrapper for API service functions with automatic error parsing and notification
 */
export async function withApiErrorHandling<T>(
  apiCall: () => Promise<AxiosResponse<T>>,
  options: {
    context?: 'login' | 'register' | 'booking' | 'payment' | 'profile' | 'general';
    showNotification?: boolean;
    notificationSource?: string;
    suppressError?: boolean;
  } = {}
): Promise<T> {
  const { 
    context = 'general', 
    showNotification = true, 
    notificationSource,
    suppressError = false 
  } = options;

  try {
    const response = await apiCall();
    
    // Handle successful response
    if (showNotification && response.data && typeof response.data === 'object' && 'message' in response.data) {
      const { addSuccessNotification } = useNotificationStore.getState();
      addSuccessNotification((response.data as any).message, notificationSource);
    }
    
    return response.data;
  } catch (error: any) {
    if (!suppressError) {
      // Log error for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('API Service Error:', formatErrorForLogging(error, context));
      }

      // Show notification if enabled
      if (showNotification) {
        const { addApiErrorNotification } = useNotificationStore.getState();
        addApiErrorNotification(error, context, notificationSource);
      }
    }
    
    throw error;
  }
}

/**
 * Extract and format field errors for form validation
 */
export function getFormFieldErrors(error: any): Record<string, string> {
  return extractFieldErrors(error);
}

/**
 * Check if an error has field-specific validation errors
 */
export function hasValidationErrors(error: any): boolean {
  const fieldErrors = extractFieldErrors(error);
  return Object.keys(fieldErrors).length > 0;
}

/**
 * Get the primary error message from API response
 */
export function getPrimaryErrorMessage(error: any, context?: string): string {
  const parsed = parseApiError(error);
  return context ? parsed.message : parsed.message;
}

/**
 * Enhanced service function decorator for React Query mutations
 */
export function createMutationWrapper<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<AxiosResponse<TData>>,
  options: {
    context?: 'login' | 'register' | 'booking' | 'payment' | 'profile' | 'general';
    successMessage?: string;
    notificationSource?: string;
  } = {}
) {
  const { context = 'general', successMessage, notificationSource } = options;

  return {
    mutationFn: async (variables: TVariables): Promise<TData> => {
      try {
        const response = await mutationFn(variables);
        
        // Show success notification if provided
        if (successMessage) {
          const { addSuccessNotification } = useNotificationStore.getState();
          addSuccessNotification(successMessage, notificationSource);
        } else if (response.data && typeof response.data === 'object' && 'message' in response.data) {
          const { addSuccessNotification } = useNotificationStore.getState();
          addSuccessNotification((response.data as any).message, notificationSource);
        }
        
        return response.data;
      } catch (error: any) {
        // Parse and show error notification
        const { addApiErrorNotification } = useNotificationStore.getState();
        addApiErrorNotification(error, context, notificationSource);
        
        throw error;
      }
    },
    onError: (error: any) => {
      // Additional error logging for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error(`${context} mutation error:`, formatErrorForLogging(error, context));
      }
    }
  };
}

/**
 * Utility to handle form submission with backend validation
 */
export async function handleFormSubmission<TData>(
  submitFn: () => Promise<AxiosResponse<TData>>,
  options: {
    onSuccess?: (data: TData) => void;
    onFieldErrors?: (errors: Record<string, string>) => void;
    context?: string;
    successMessage?: string;
  } = {}
): Promise<{ success: boolean; data?: TData; fieldErrors?: Record<string, string> }> {
  
  const { onSuccess, onFieldErrors, context = 'form', successMessage } = options;

  try {
    const response = await submitFn();
    
    // Handle success
    if (successMessage) {
      const { addSuccessNotification } = useNotificationStore.getState();
      addSuccessNotification(successMessage);
    }
    
    if (onSuccess) {
      onSuccess(response.data);
    }
    
    return { success: true, data: response.data };
    
  } catch (error: any) {
    // Check for field validation errors
    const fieldErrors = extractFieldErrors(error);
    
    if (Object.keys(fieldErrors).length > 0) {
      // Handle field validation errors
      if (onFieldErrors) {
        onFieldErrors(fieldErrors);
      }
      
      return { success: false, fieldErrors };
    } else {
      // Handle general errors
      const { addApiErrorNotification } = useNotificationStore.getState();
      addApiErrorNotification(error, context);
      
      return { success: false };
    }
  }
}

/**
 * Create a standardized error handler for React Hook Form
 */
export function createFormErrorHandler(setError: any) {
  return (error: any) => {
    const fieldErrors = extractFieldErrors(error);
    
    // Set field-specific errors
    Object.entries(fieldErrors).forEach(([field, message]) => {
      setError(field, {
        type: 'server',
        message: message
      });
    });
    
    // If no field errors, show general notification
    if (Object.keys(fieldErrors).length === 0) {
      const { addApiErrorNotification } = useNotificationStore.getState();
      addApiErrorNotification(error, 'form');
    }
  };
}

/**
 * Utility for handling backend file upload responses
 */
export async function handleFileUpload(
  uploadFn: () => Promise<AxiosResponse<any>>,
  options: {
    onProgress?: (progress: number) => void;
    onSuccess?: (data: any) => void;
    successMessage?: string;
  } = {}
): Promise<{ success: boolean; data?: any }> {
  
  const { onSuccess, successMessage } = options;

  try {
    const response = await uploadFn();
    
    if (successMessage) {
      const { addSuccessNotification } = useNotificationStore.getState();
      addSuccessNotification(successMessage);
    }
    
    if (onSuccess) {
      onSuccess(response.data);
    }
    
    return { success: true, data: response.data };
    
  } catch (error: any) {
    const { addApiErrorNotification } = useNotificationStore.getState();
    addApiErrorNotification(error, 'general');
    
    return { success: false };
  }
}